import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { Button } from "../Button";
import { Popover } from "../Popover";

import styles from "./Autocomplete.scss";

interface ScrollOffset {
  x: number;
  y: number;
}

function getScrollOffset(): ScrollOffset {
  return {
    x: window.scrollX,
    y: window.scrollY,
  };
}

/**
 * This is a legacy-style React component that we copied into this codebase from
 * a now-archived NPM package. The functionality here works well, but we had to
 * copy it and modify it to work with newer React versions.
 *
 * We thought that since people like Ryan Florence were maintaining it, it would
 * last a while, but alas, a few months after we started using it, it was archived!
 *
 * https://github.com/reactjs/react-autocomplete
 */

export interface AutocompleteProps<T = any> {
  /**
   * If you want an index to be highlighted by default, pass it here.
   * Not super useful in the wild, but useful for testing.
   */
  defaultHighlightedIndex?: number;
  /**
   * The items to display in the dropdown menu
   */
  items: T[];
  /**
   * The value to display in the input field
   */
  value?: any;
  /**
   * Arguments: `event: Event, value: String`
   *
   * Invoked every time the user changes the input's value.
   */
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => void;
  /**
   * Arguments: `value: String, item: Any`
   *
   * Invoked when the user selects an item from the dropdown menu.
   */
  onSelect?: (value: string, item: T) => void;
  /**
   * Arguments: `item: Any, value: String`
   *
   * Invoked for each entry in `items` and its return value is used to
   * determine whether or not it should be displayed in the dropdown menu.
   * By default all items are always rendered.
   */
  shouldItemRender?: (item: T, value: string) => boolean;
  /**
   * Arguments: `item: Any`
   *
   * Invoked when attempting to select an item. The return value is used to
   * determine whether the item should be selectable or not.
   * By default all items are selectable.
   */
  isItemSelectable?: (item: T) => boolean;
  /**
   * Arguments: `itemA: Any, itemB: Any, value: String`
   *
   * The function which is used to sort `items` before display.
   */
  sortItems?: (a: T, b: T, value: string) => number;
  /**
   * Arguments: `item: Any`
   *
   * Used to read the display value from each entry in `items`.
   */
  getItemValue: (item: T) => string;
  /**
   * Arguments: `item: Any, isHighlighted: Boolean, styles: Object`
   *
   * Invoked for each entry in `items` that also passes `shouldItemRender` to
   * generate the render tree for each item in the dropdown menu. `styles` is
   * an optional set of styles that can be applied to improve the look/feel
   * of the items in the dropdown menu.
   */
  renderItem: (
    item: T,
    isHighlighted: boolean,
    styles: {
      cursor: string;
      currentInputValue: string;
    },
    index: number
  ) => React.ReactElement | React.ReactNode;
  /**
   * Arguments: `items: Array<Any>, value: String, styles: Object`
   *
   * Invoked to generate the render tree for the dropdown menu. Ensure the
   * returned tree includes every entry in `items` or else the highlight order
   * and keyboard navigation logic will break. `styles` will contain
   * { top, left, minWidth } which are the coordinates of the top-left corner
   * and the width of the dropdown menu.
   */
  renderMenu?: (
    items: React.ReactElement[],
    value: string,
    style: {
      left?: number;
      top?: number;
      minWidth?: number;
    }
  ) => React.ReactElement;
  /**
   * Styles that are applied to the dropdown menu in the default `renderMenu`
   * implementation. If you override `renderMenu` and you want to use
   * `menuStyle` you must manually apply them (`this.props.menuStyle`).
   */
  menuStyle?: React.CSSProperties;
  /**
   * Arguments: `props: Object`
   *
   * Invoked to generate the input element. The `props` argument is the result
   * of merging `props.inputProps` with a selection of props that are required
   * both for functionality and accessibility. At the very least you need to
   * apply `props.ref` and all `props.on<event>` event handlers. Failing to do
   * this will cause `Autocomplete` to behave unexpectedly.
   */
  renderInput?: (props: any) => React.ReactElement;
  /**
   * Props passed to `props.renderInput`. By default these props will be
   * applied to the `<input />` element rendered by `Autocomplete`, unless you
   * have specified a custom value for `props.renderInput`. Any properties
   * supported by `HTMLInputElement` can be specified, apart from the
   * following which are set by `Autocomplete`: value, autoComplete, role,
   * aria-autocomplete. `inputProps` is commonly used for (but not limited to)
   * placeholder, event handlers (onFocus, onBlur, etc.), autoFocus, etc..
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /**
   * Props that are applied to the element which wraps the `<input />` and
   * dropdown menu elements rendered by `Autocomplete`.
   */
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  /**
   * This is a shorthand for `wrapperProps={{ style: <your styles> }}`.
   * Note that `wrapperStyle` is applied before `wrapperProps`, so the latter
   * will win if it contains a `style` entry.
   */
  wrapperStyle?: React.CSSProperties;
  /**
   * Whether or not to automatically select the highlighted item when the
   * `<input>` loses focus.
   */
  selectOnBlur?: boolean;
  /**
   * Arguments: `isOpen: Boolean`
   *
   * Invoked every time the dropdown menu's visibility changes (i.e. every
   * time it is displayed/hidden).
   */
  onMenuVisibilityChange?: (isOpen: boolean) => void;
  /**
   * Used to override the internal logic which displays/hides the dropdown
   * menu. This is useful if you want to force a certain state based on your
   * UX/business logic. Use it together with `onMenuVisibilityChange` for
   * fine-grained control over the dropdown menu dynamics.
   */
  open?: any;
  /**
   * To skip the onClick and onEnter handler in a list element.
   * Sometimes we want to handle this event outside this component.
   * If you skip them, they won't be added to every list element.
   */
  skipEventHandlers?: boolean;
  keepInputAfterSelection?: boolean;
  /**
   * It will position the `renderMenu` prop the best way possible.
   * At the end, it uses Popper.JS to best place the popped-element
   * */
  popperEnabled?: boolean;
  /**
   * Autocomplete used for <MultiSelect /> where a caret-button is used.
   * It works together with `actionButtonEnabled`
   * */
  isMultiSelect?: boolean;
  /**
   * Whether or not the input caret will be displayed
   */
  hideInputCaret?: boolean;
  /**
   * Label for the chevron button when it is open (default value is Open)
   */
  buttonOpenLabel?: string;
  /**
   * Label for the chevron button when it is closed (default value is Closed)
   */
  buttonClosedLabel?: string;
  /**
   * Whether or not the menu should be rendered in a portal
   */
  renderInPortal?: boolean;
}

interface AutocompleteState {
  isOpen: boolean;
  highlightedIndex: number | null;
  menuTop?: number;
  menuLeft?: number;
  menuWidth?: number;
}

interface AutocompleteRefs {
  input?: HTMLInputElement | null;
  menu?: HTMLElement | null;
  [key: string]: HTMLElement | null | undefined;
}

export class Autocomplete<T = any> extends React.Component<
  AutocompleteProps<T>,
  AutocompleteState
> {
  static displayName = "Autocomplete";

  static defaultProps = {
    value: "",
    wrapperProps: {},
    wrapperStyle: {
      display: "inline-block",
    },
    inputProps: {},
    renderInput({
      // This is required because regular HTML elements can only receive lowercased custom attributes
      // Source:
      // https://react.dev/reference/react-dom/components/common#common-props
      // https://legacy.reactjs.org/docs/dom-elements.html
      // https://legacy.reactjs.org/blog/2017/09/08/dom-attributes-in-react-16.html
      highlightedIndex, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...props
    }: any) {
      return <input {...props} />;
    },
    onChange() {},
    isItemSelectable() {
      return true;
    },
    renderMenu(items: React.ReactElement[], value: string, style: any) {
      return (
        <div
          style={{ ...style, ...this.menuStyle }}
          data-testid="autocomplete-menu"
        >
          {items}
        </div>
      );
    },
    menuStyle: {
      borderRadius: "3px",
      boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
      background: "rgba(255, 255, 255, 0.9)",
      padding: "2px 0",
      fontSize: "90%",
      position: "fixed" as const,
      overflow: "auto",
      maxHeight: "50%", // TODO: don't cheat, let it flow to the bottom
    },
    selectOnBlur: false,
    onMenuVisibilityChange() {},
    keepInputAfterSelection: false,
    skipEventHandlers: false,
    popperEnabled: false,
    hideInputCaret: false,
    buttonOpenLabel: "Open",
    buttonClosedLabel: "Closed",
    renderInPortal: true,
  };

  private _refs: AutocompleteRefs = {};
  private _ignoreBlur = false;
  private _ignoreFocus = false;
  private _scrollOffset: ScrollOffset | null = null;
  private _scrollTimer: NodeJS.Timeout | null = null;
  private _actionButtonEnabled = true;

  // Imperative API methods that will be dynamically assigned
  blur?: () => void;
  checkValidity?: () => boolean;
  click?: () => void;
  focus?: () => void;
  select?: () => void;
  setCustomValidity?: (error: string) => void;
  setSelectionRange?: (
    start: number | null,
    end: number | null,
    direction?: "forward" | "backward" | "none"
  ) => void;
  setRangeText?: (
    replacement: string,
    start?: number,
    end?: number,
    selectMode?: string
  ) => void;

  constructor(props: AutocompleteProps<T>) {
    super(props);
    this.state = {
      isOpen: false,
      highlightedIndex: props.defaultHighlightedIndex || null,
    };
    this.ensureHighlightedIndex = this.ensureHighlightedIndex.bind(this);
    this.exposeAPI = this.exposeAPI.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleInputClick = this.handleInputClick.bind(this);
    this.maybeHighlightClosestMatch =
      this.maybeHighlightClosestMatch.bind(this);
  }

  override componentWillUnmount(): void {
    if (this._scrollTimer) {
      clearTimeout(this._scrollTimer);
      this._scrollTimer = null;
    }
  }

  override componentDidMount(): void {
    if (this.isOpen()) {
      this.setMenuPositions();
    }
  }

  override componentDidUpdate(
    prevProps: AutocompleteProps<T>,
    prevState: AutocompleteState
  ): void {
    if (
      (this.state.isOpen && !prevState.isOpen) ||
      ("open" in this.props && this.props.open && !prevProps.open)
    ) {
      this.setMenuPositions();
    }

    if (prevState.isOpen !== this.state.isOpen) {
      this.props.onMenuVisibilityChange?.(this.state.isOpen);
      this.maybeHighlightClosestMatch();
    }

    this.ensureHighlightedIndex();
  }

  exposeAPI(el: HTMLInputElement | null): void {
    this._refs.input = el;
    if (!el) {
      return;
    }

    // Explicitly assign each imperative API method with proper type checking
    // istanbul ignore else
    if (typeof el.blur === "function") {
      this.blur = el.blur.bind(el);
    }
    // istanbul ignore else
    if (typeof el.checkValidity === "function") {
      this.checkValidity = el.checkValidity.bind(el);
    }
    // istanbul ignore else
    if (typeof el.click === "function") {
      this.click = el.click.bind(el);
    }
    // istanbul ignore else
    if (typeof el.focus === "function") {
      this.focus = el.focus.bind(el);
    }
    // istanbul ignore else
    if (typeof el.select === "function") {
      this.select = el.select.bind(el);
    }
    // istanbul ignore else
    if (typeof el.setCustomValidity === "function") {
      this.setCustomValidity = el.setCustomValidity.bind(el);
    }
    // istanbul ignore else
    if (typeof el.setSelectionRange === "function") {
      this.setSelectionRange = el.setSelectionRange.bind(el);
    }
    // istanbul ignore else
    if (typeof el.setRangeText === "function") {
      this.setRangeText = el.setRangeText.bind(el);
    }
  }

  handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    // Type guard function to check if a key exists in keyDownHandlers
    const isValidKey = (
      key: string
    ): key is keyof typeof Autocomplete.keyDownHandlers => {
      return key in Autocomplete.keyDownHandlers;
    };

    if (isValidKey(event.key)) {
      this._ignoreBlur = true;
      const handler = Autocomplete.keyDownHandlers[event.key];
      handler.call(this, event);
      return;
    }

    if (!this.isOpen()) {
      this.setState({
        isOpen: true,
      });
    }
    this.maybeHighlightClosestMatch();
    this.ensureHighlightedIndex();
  }

  handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    this.props.onChange?.(event, event.target.value);
  }

  scrollNodeIntoView(nodeRef: HTMLElement): void {
    const menu = this._refs.menu;
    if (!menu) {
      return;
    }
    // If the node is below the visible area, scroll down enough to bring the entire node into view.
    const menuBottomEdge = menu.scrollTop + menu.clientHeight;
    if (nodeRef.offsetTop >= menuBottomEdge) {
      const nodeBottomEdge = nodeRef.offsetTop + nodeRef.offsetHeight;
      menu.scrollTop += nodeBottomEdge - menuBottomEdge;
    }
    // If the node is above the visible area, scroll up to it.
    if (nodeRef.offsetTop < menu.scrollTop) {
      menu.scrollTop = nodeRef.offsetTop;
    }
  }

  static keyDownHandlers: {
    ArrowDown: (
      this: Autocomplete<any>,
      event: React.KeyboardEvent<HTMLInputElement>
    ) => void;
    ArrowUp: (
      this: Autocomplete<any>,
      event: React.KeyboardEvent<HTMLInputElement>
    ) => void;
    Enter: (
      this: Autocomplete<any>,
      event: React.KeyboardEvent<HTMLInputElement>
    ) => void;
    Escape: (this: Autocomplete<any>) => void;
    Tab: (this: Autocomplete<any>) => void;
  } = {
    ArrowDown(event: React.KeyboardEvent<HTMLInputElement>): void {
      event.preventDefault();
      const items = this.getFilteredItems();
      if (!items.length) {
        return;
      }
      const { highlightedIndex } = this.state;
      let index = highlightedIndex === null ? -1 : highlightedIndex;
      for (let i = 0; i < items.length; i++) {
        const p = (index + i + 1) % items.length;
        if (this.props.isItemSelectable?.(items[p])) {
          index = p;
          break;
        }
      }
      // istanbul ignore else
      if (index > -1 && index !== highlightedIndex) {
        const itemNode = this._refs[`item-${index}`];
        // istanbul ignore else
        if (itemNode) {
          this.scrollNodeIntoView(itemNode);
        }
        this.setState({
          highlightedIndex: index,
          isOpen: true,
        });
      }
    },

    ArrowUp(event: React.KeyboardEvent<HTMLInputElement>): void {
      event.preventDefault();
      const items = this.getFilteredItems();
      if (!items.length) {
        return;
      }
      const { highlightedIndex } = this.state;
      if (highlightedIndex === 0) {
        // clear suggestions if the user taps ArrowUp at the top of the list rather than wrapping (which makes it
        // impossible to "escape" the suggestion list with keyboard input)
        this.setState({ highlightedIndex: null });
        return;
      }
      let index = highlightedIndex === null ? items.length : highlightedIndex;
      for (let i = 0; i < items.length; i++) {
        const p = (index - (1 + i) + items.length) % items.length;
        if (this.props.isItemSelectable?.(items[p])) {
          index = p;
          break;
        }
      }
      // istanbul ignore else
      if (index !== items.length) {
        const itemNode = this._refs[`item-${index}`];
        // istanbul ignore else
        if (itemNode) {
          this.scrollNodeIntoView(itemNode);
        }
        this.setState({
          highlightedIndex: index,
          isOpen: true,
        });
      }
    },

    Enter(event: React.KeyboardEvent<HTMLInputElement>): void {
      // Key code 229 is used for selecting items from character selectors (Pinyin, Kana, etc)
      const keyboardEvent = event.nativeEvent;
      if (
        keyboardEvent &&
        "keyCode" in keyboardEvent &&
        keyboardEvent.keyCode !== 13
      ) {
        // In case the user is currently hovering over the menu
        this._ignoreBlur = false;
        return;
      }
      // In case the user is currently hovering over the menu
      this._ignoreBlur = false;
      if (!this.isOpen()) {
        // menu is closed so there is no selection to accept -> do nothing
        return;
      } else if (this.state.highlightedIndex == null) {
        // input has focus but no menu item is selected + enter is hit -> close the menu, highlight whatever's in input
        this.setState(
          {
            isOpen: false,
          },
          () => {
            this._refs.input?.select();
          }
        );
      } else {
        // text entered + menu item has been highlighted + enter is hit -> update value to that of selected menu item, close the menu
        event.preventDefault();
        const item = this.getFilteredItems()[this.state.highlightedIndex];
        const value = this.props.getItemValue(item);
        this.setState(
          {
            isOpen: this.props.keepInputAfterSelection || false,
            highlightedIndex: null,
          },
          () => {
            this._refs.input?.focus();
            this._refs.input?.setSelectionRange(value.length, value.length);
            this.props.onSelect?.(value, item);
          }
        );
      }
    },

    Escape(): void {
      // In case the user is currently hovering over the menu
      this._ignoreBlur = false;
      this.setState({
        highlightedIndex: null,
        isOpen: false,
      });
    },

    Tab(): void {
      // In case the user is currently hovering over the menu
      this._ignoreBlur = false;
    },
  };

  getFilteredItems(): T[] {
    let items = this.props.items;

    if (this.props.shouldItemRender) {
      items = items.filter((item) =>
        coalesce(
          this.props.shouldItemRender?.(item, coalesce(this.props.value, "")),
          true
        )
      );
    }

    if (this.props.sortItems) {
      items.sort((a, b) =>
        coalesce(this.props.sortItems?.(a, b, this.props.value || ""), 0)
      );
    }

    return items;
  }

  maybeHighlightClosestMatch(): void {
    const { highlightedIndex } = this.state;
    const { value, getItemValue } = this.props;
    const items = this.getFilteredItems();

    if (value === "") {
      return;
    }

    // Track the new index that needs to be selected based on highlighted results
    let newIndex = highlightedIndex;
    for (let i = 0; i < items.length; i++) {
      if (!this.props.isItemSelectable?.(items[i])) {
        continue;
      }
      const itemValue = getItemValue(items[i]);
      const itemValueDoesMatch =
        itemValue.toLowerCase().indexOf(coalesce(value, "").toLowerCase()) ===
        0;
      if (itemValueDoesMatch) {
        newIndex = i;
        break;
      }
    }

    return this.setState({ highlightedIndex: newIndex });
  }

  ensureHighlightedIndex(): void {
    if (
      this.state.highlightedIndex &&
      this.state.highlightedIndex >= this.getFilteredItems().length
    ) {
      this.setState({ highlightedIndex: null });
    }
  }

  setMenuPositions(): void {
    const node = this._refs.input;
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(node);
    const marginBottom = parseInt(computedStyle.marginBottom, 10) || 0;
    const marginLeft = parseInt(computedStyle.marginLeft, 10) || 0;
    const marginRight = parseInt(computedStyle.marginRight, 10) || 0;
    this.setState({
      menuTop: rect.bottom + marginBottom,
      menuLeft: rect.left + marginLeft,
      menuWidth: rect.width + marginLeft + marginRight,
    });
  }

  highlightItemFromMouse(index: number): void {
    this.setState({ highlightedIndex: index });
  }

  selectItemFromMouse(item: T): void {
    const value = this.props.getItemValue(item);

    // The menu will de-render before a mouseLeave event
    // happens. Clear the flag to release control over focus
    this._ignoreBlur = this.props.keepInputAfterSelection || false;
    this.setState(
      {
        isOpen: this.props.keepInputAfterSelection || false,
        highlightedIndex: null,
      },
      () => {
        this.props.onSelect?.(value, item);
      }
    );
  }

  private handleMenuTouchStart = (): void => {
    this._ignoreBlur = true;
  };

  private handleMenuMouseEnter = (): void => {
    this._ignoreBlur = true;
  };

  private handleMenuMouseLeave = (): void => {
    this._ignoreBlur = false;
  };

  renderMenu(): React.ReactElement {
    const items = this.getFilteredItems()
      .map((item, index) => {
        const element = this.props.renderItem(
          item,
          this.state.highlightedIndex === index,
          {
            cursor: "default",
            currentInputValue: this.props.value || "",
          },
          index
        );

        // Handle case where renderItem returns undefined or null
        if (!element || !React.isValidElement(element)) {
          return null;
        }

        let eventHandlers: any = {};
        // istanbul ignore else - noop
        if (!this.props.skipEventHandlers) {
          eventHandlers = {
            onMouseEnter: this.props.isItemSelectable?.(item)
              ? () => this.highlightItemFromMouse(index)
              : null,
            onClick: this.props.isItemSelectable?.(item)
              ? () => this.selectItemFromMouse(item)
              : null,
          };
        }
        return React.cloneElement(element, {
          ...eventHandlers,
          ref: (e: HTMLElement | null) => (this._refs[`item-${index}`] = e),
        });
      })
      .filter((item): item is React.ReactElement => item !== null);

    const style = {
      left: this.state.menuLeft,
      top: this.state.menuTop,
      minWidth: this.state.menuWidth,
    };
    const menu = coalesce(
      this.props.renderMenu?.(items, coalesce(this.props.value, ""), style),
      Autocomplete.defaultProps.renderMenu(items, this.props.value || "", style)
    );
    return React.cloneElement(menu, {
      ref: (e: HTMLElement | null) => (this._refs.menu = e),
      // Ignore blur to prevent menu from de-rendering before we can process click
      onTouchStart: this.handleMenuTouchStart,
      onMouseEnter: this.handleMenuMouseEnter,
      onMouseLeave: this.handleMenuMouseLeave,
    });
  }

  handleInputBlur(event: React.FocusEvent<HTMLInputElement>): void {
    // Clear any pending scroll timers when blurring, regardless of _ignoreBlur state

    // istanbul ignore next
    if (this._scrollTimer) {
      // istanbul ignore next
      clearTimeout(this._scrollTimer);
    }

    if (this._ignoreBlur) {
      this._ignoreFocus = true;
      this._scrollOffset = getScrollOffset();

      // Re-focus the input to maintain the dropdown state
      this._refs.input?.focus();
      return;
    }

    /**
     * If the user blurs the input by clicking the multi-select button, use some
     * local state to tell the onClick handler to not steal focus from the input.
     */
    if (
      document.getElementById("multiselect-caret-btn") === event?.relatedTarget
    ) {
      this._actionButtonEnabled = false;
    }

    let setStateCallback: (() => void) | undefined;
    const { highlightedIndex } = this.state;
    if (this.props.selectOnBlur && highlightedIndex !== null) {
      const items = this.getFilteredItems();
      const item = items[highlightedIndex];
      const value = this.props.getItemValue(item);
      setStateCallback = () => this.props.onSelect?.(value, item);
    }
    this.setState(
      {
        isOpen: false,
        highlightedIndex: null,
      },
      setStateCallback
    );
    const { onBlur } = coalesce(this.props.inputProps, {});
    if (onBlur) {
      onBlur(event);
    }
  }

  handleInputFocus(event: React.FocusEvent<HTMLInputElement>): void {
    if (this._ignoreFocus) {
      this._ignoreFocus = false;
      const { x, y } = this._scrollOffset || { x: 0, y: 0 };
      this._scrollOffset = null;
      // Focus will cause the browser to scroll the <input> into view.
      // This can cause the mouse coords to change, which in turn
      // could cause a new highlight to happen, cancelling the click
      // event (when selecting with the mouse)
      window.scrollTo(x, y);
      // Some browsers wait until all focus event handlers have been
      // processed before scrolling the <input> into view, so let's
      // scroll again on the next tick to ensure we're back to where
      // the user was before focus was lost. We could do the deferred
      // scroll only, but that causes a jarring split second jump in
      // some browsers that scroll before the focus event handlers
      // are triggered.
      if (this._scrollTimer) {
        clearTimeout(this._scrollTimer);
      }
      this._scrollTimer = setTimeout(() => {
        this._scrollTimer = null;
        window.scrollTo(x, y);
      }, 0);
      return;
    }
    this.setState({ isOpen: true });
    const { onFocus } = coalesce(this.props.inputProps, {});
    if (onFocus) {
      onFocus(event);
    }
  }

  isInputFocused(): boolean {
    const el = this._refs.input;
    return !!(el?.ownerDocument && el === el.ownerDocument.activeElement);
  }

  handleInputClick(): void {
    // Input will not be focused if it's disabled
    if (this.isInputFocused() && !this.isOpen()) {
      this.setState({ isOpen: true });
    }
  }

  composeEventHandlers(
    internal: (e: any) => void,
    external?: (e: any) => void
  ): (e: any) => void {
    return external
      ? (e) => {
          internal(e);
          if (!e.defaultPrevented) {
            external(e);
          }
        }
      : internal;
  }

  isOpen(): boolean {
    return "open" in this.props ? !!this.props.open : this.state.isOpen;
  }

  renderButton(): React.ReactElement | null {
    const { isMultiSelect, buttonOpenLabel, buttonClosedLabel } = this.props;
    if (!isMultiSelect) {
      return null;
    }
    if (this.props.hideInputCaret) {
      return null;
    }

    const selectIcon = this.isOpen() ? "caret_up" : "caret_dn";

    const onClickHandler = () => {
      /**
       * If we click on the button and the menu isn't open, no big deal! Let's
       * enable it.
       *
       * But if we click on the button and the menu is open, we don't want to
       * steal the focus from the input. So we disable the button's normal
       * behavior until the user clicks again.
       */
      if (this._actionButtonEnabled) {
        this._refs.input?.focus();
      } else {
        this._actionButtonEnabled = true;
      }
    };

    return (
      <Button
        id="multiselect-caret-btn"
        data-testid="multiselect-caret-btn-test"
        type="button"
        icon={selectIcon}
        size="default"
        className={styles["trigger-button-icon"]}
        color="var(--confetti-text-secondary)"
        onClick={onClickHandler}
        aria-label={this.isOpen() ? buttonOpenLabel : buttonClosedLabel}
        title={this.isOpen() ? buttonOpenLabel : buttonClosedLabel}
      />
    );
  }

  override render(): React.ReactElement {
    const {
      inputProps,
      renderInput,
      value,
      popperEnabled,
      wrapperStyle,
      wrapperProps,
      renderInPortal,
    } = this.props;

    const open = this.isOpen();
    if (!open && this._ignoreBlur) {
      this._ignoreBlur = false;
    }

    const inputRenderer = renderInput || Autocomplete.defaultProps.renderInput;
    const inputComponent = inputRenderer({
      ...inputProps,
      "aria-autocomplete": "list",
      autoComplete: "off",
      ref: this.exposeAPI,
      onFocus: this.handleInputFocus,
      onBlur: this.handleInputBlur,
      onChange: this.handleChange,
      onKeyDown: this.composeEventHandlers(
        this.handleKeyDown,
        inputProps?.onKeyDown
      ),
      onClick: this.composeEventHandlers(
        this.handleInputClick,
        inputProps?.onClick
      ),
      highlightedIndex: this.state.highlightedIndex,
      value: value || "",
    });

    if (!inputComponent) {
      // Fallback if no renderInput is available
      return <input {...inputProps} value={value || ""} />;
    }

    const componentStyles = cx(wrapperStyle, wrapperProps, {
      [styles["popper-wrapper"]]: popperEnabled,
      [styles["relative-wrapper"]]: !popperEnabled,
    });

    return popperEnabled ? (
      <>
        <Popover
          isOpen={open}
          parent={inputComponent}
          popoverComponent={this.renderMenu()}
          className={componentStyles}
          position="bottom"
          data-testid="meatBallMenu-popover"
          popperProps={{
            className: styles["full-width"],
            sameWidth: true,
            renderInPortal,
          }}
        />
      </>
    ) : (
      <div className={componentStyles}>
        {inputComponent}
        {this.renderButton()}
        {open && this.renderMenu()}
      </div>
    );
  }
}
